import { Component, Inject, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LeaderService } from 'src/app/leader-toolkit/services/leader.service';
import { map, startWith } from 'rxjs/operators';

@Component({
  selector: 'app-new-meeting-scheduler',
  standalone: false,
  providers: [DatePipe],
  templateUrl: './new-meeting-scheduler.component.html',
  styleUrls: ['./new-meeting-scheduler.component.scss'],
})

export class NewMeetingSchedulerComponent implements OnInit {
  meetingForm!: FormGroup;
  minDate: Date;
  loading = false;
  isEditMode = false;
  editMeetingData: any = null;
  hospitals = [];
  timeZoneOptions = [
    { label: 'Eastern Time (US & Canada)', value: 'Eastern Standard Time' },
    { label: 'Central Time (US & Canada)', value: 'Central Standard Time' },
    { label: 'Mountain Time (US & Canada)', value: 'Mountain Standard Time' },
    { label: 'Pacific Time (US & Canada)', value: 'Pacific Standard Time' },
    { label: 'Hawaii Time', value: 'Hawaiian Standard Time' },
  ];
  meetingTypeOptions = [
    { label: 'In-Person', value: 'In-Person' },
    { label: 'Virtual', value: 'Virtual' },
  ];
  frequencyOptions = [
    { label: 'days', value: 'daily' },
    { label: 'weeks', value: 'weekly' },
    { label: 'months', value: 'monthly' },
    { label: 'years', value: 'yearly' },
  ];
  dayOfWeekOptions = [
    { label: 'S', value: 'Sun', full: 'Sunday' },
    { label: 'M', value: 'Mon', full: 'Monday' },
    { label: 'T', value: 'Tue', full: 'Tuesday' },
    { label: 'W', value: 'Wed', full: 'Wednesday' },
    { label: 'T', value: 'Thu', full: 'Thursday' },
    { label: 'F', value: 'Fri', full: 'Friday' },
    { label: 'S', value: 'Sat', full: 'Saturday' },
  ];
  filteredHospitalList: any[] = [];
  goalList: any[] = [];
  quarterList: any[] = [];
  formSubmitted: boolean = false;
  upcomingOccurrences: { date: Date; display: string }[] = [];
  showOccurrences: boolean = false;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<NewMeetingSchedulerComponent>,
    private datePipe: DatePipe,
    public leaderService: LeaderService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.isEditMode = data?.meetingData != null;
    this.editMeetingData = data?.meetingData || null;
    this.hospitals = data?.hospitals || data || [];
    this.filteredHospitalList = [...this.hospitals];
    this.minDate = new Date();
  }

  ngOnInit(): void {
    const now = new Date();
    const start = new Date(now);
    start.setMinutes(0, 0, 0);
    if (now.getMinutes() > 0 || now.getSeconds() > 0 || now.getMilliseconds() > 0) {
      start.setHours(start.getHours() + 1);
    }
    const end = new Date(start.getTime());
    end.setHours(start.getHours() + 1);

    this.meetingForm = this.fb.group({
      meetingSubject: ['', Validators.required],
      hospital: [null],
      hospitalId: [null],
      quarter: [null],
      affiliatedGoal: [0],
      scheduledDate: [new Date(), Validators.required],
      startTime: [start, Validators.required],
      endTime: [end, Validators.required],
      timezone: ['', Validators.required],
      meetingType: [null, Validators.required],
      attendeesIds: [[], Validators.required],
      agenda: [''],
      isRecurring: [false],
      recurrenceInterval: [1],
      recurrenceFrequency: [null as string | null],
      recurrenceDaysOfWeek: [[] as string[]],
      recurrenceEndDate: [null]
    }, { validators: this.timeOrderValidator });

    this.filteredHospitalList = [...this.hospitals];
    this.setupHospitalAutocomplete();
    this.qtrList();
    this.meetingForm.get('hospital')?.valueChanges.subscribe(() => this.onHospitalChange());
    this.meetingForm.get('quarter')?.valueChanges.subscribe(() => this.onQuarterChange());

    this.meetingForm.get('recurrenceFrequency')!.valueChanges.subscribe(() => this.generateOccurrences());
    this.meetingForm.get('recurrenceInterval')!.valueChanges.subscribe(() => this.generateOccurrences());
    this.meetingForm.get('recurrenceDaysOfWeek')!.valueChanges.subscribe(() => this.generateOccurrences());
    this.meetingForm.get('recurrenceEndDate')!.valueChanges.subscribe(() => this.generateOccurrences());
    this.meetingForm.get('scheduledDate')!.valueChanges.subscribe(() => this.generateOccurrences());

    if (this.isEditMode && this.editMeetingData) {
      this.populateFormForEdit();
      // Disable recurrence controls in edit mode — recurrence is read-only
      if (this.editMeetingData.isRecurring) {
        this.meetingForm.get('isRecurring')?.disable();
        this.meetingForm.get('recurrenceInterval')?.disable();
        this.meetingForm.get('recurrenceFrequency')?.disable();
        this.meetingForm.get('recurrenceDaysOfWeek')?.disable();
        this.meetingForm.get('recurrenceEndDate')?.disable();
      }
    }
  }

  private populateFormForEdit(): void {
    const m = this.editMeetingData;
    const startTime = this.parseTime(m.startTime);
    const endTime = this.parseTime(m.endTime);

    const daysOfWeek = m.recurrenceDaysOfWeek
      ? m.recurrenceDaysOfWeek.split(',').map((d: string) => d.trim())
      : this.inferDaysOfWeek(m.recurrencePattern);

    this.meetingForm.patchValue({
      meetingSubject: m.meetingSubject || '',
      scheduledDate: m.scheduledDate ? this.parseLocalDate(m.scheduledDate) : new Date(),
      startTime: startTime,
      endTime: endTime,
      timezone: m.timeZone || '',
      meetingType: m.meetingType || null,
      agenda: m.agenda || '',
      attendeesIds: (m.attendeesIds || []).map((id: number, i: number) => ({
        id,
        displayName: (m.attendeesNames || [])[i] || ''
      })),
      isRecurring: m.isRecurring || false,
      recurrenceInterval: m.recurrenceInterval || 1,
      recurrenceFrequency: m.recurrenceFrequency || m.recurrencePattern || null,
      recurrenceDaysOfWeek: daysOfWeek,
      recurrenceEndDate: m.recurrenceEndDate ? this.parseLocalDate(m.recurrenceEndDate) : null
    });

    if (m.hospitalId && this.hospitals.length > 0) {
      const hospital = this.hospitals.find((h: any) => h.practiceSKey === m.hospitalId);
      if (hospital) {
        this.meetingForm.patchValue({ hospital });
        this.meetingForm.patchValue({ hospitalId: m.hospitalId }, { emitEvent: false });
        this.trySetQuarterAndGoal();
      }
    }
  }

  private inferDaysOfWeek(pattern: string): string[] {
    if (!pattern) return [];
    const p = pattern.toLowerCase();
    if (p === 'biweekly' || p === 'bi-weekly') return ['Mon'];
    return [];
  }

  toggleDayOfWeek(day: string): void {
    const current: string[] = this.meetingForm.get('recurrenceDaysOfWeek')?.value || [];
    const idx = current.indexOf(day);
    if (idx >= 0) {
      current.splice(idx, 1);
    } else {
      current.push(day);
    }
    this.meetingForm.get('recurrenceDaysOfWeek')?.setValue([...current], { emitEvent: true });
  }

  isDaySelected(day: string): boolean {
    const current: string[] = this.meetingForm.get('recurrenceDaysOfWeek')?.value || [];
    return current.includes(day);
  }

  get isWeeklyFrequency(): boolean {
    return this.meetingForm.get('recurrenceFrequency')?.value === 'weekly';
  }

  generateOccurrences(): void {
    const freq = this.meetingForm.get('recurrenceFrequency')?.value;
    const interval = this.meetingForm.get('recurrenceInterval')?.value || 1;
    const endDate = this.meetingForm.get('recurrenceEndDate')?.value;
    const scheduledDate = this.meetingForm.get('scheduledDate')?.value;
    const daysOfWeek: string[] = this.meetingForm.get('recurrenceDaysOfWeek')?.value || [];

    if (!freq || !endDate || !scheduledDate) {
      this.upcomingOccurrences = [];
      return;
    }

    const start = new Date(scheduledDate);
    const end = new Date(endDate);
    const occurrences: { date: Date; display: string }[] = [];
    let current = new Date(start);
    let iterations = 0;

    while (current <= end && iterations < 52) {
      iterations++;

      if (freq === 'weekly' && daysOfWeek.length > 0) {
        const weekStart = new Date(current);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay());

        for (const day of daysOfWeek) {
          const dayIdx = this.dayOfWeekOptions.findIndex(d => d.value === day);
          if (dayIdx < 0) continue;

          const occDate = new Date(weekStart);
          occDate.setDate(occDate.getDate() + dayIdx);

          if (occDate >= start && occDate <= end) {
            occurrences.push({
              date: new Date(occDate),
              display: this.datePipe.transform(occDate, 'EEE, MMM d, yyyy') || ''
            });
          }
          if (occurrences.length >= 20) break;
        }
      } else {
        if (current >= start && current <= end) {
          occurrences.push({
            date: new Date(current),
            display: this.datePipe.transform(current, 'EEE, MMM d, yyyy') || ''
          });
        }
      }

      if (occurrences.length >= 20) break;

      switch (freq) {
        case 'daily':
          current.setDate(current.getDate() + interval);
          break;
        case 'weekly':
          current.setDate(current.getDate() + 7 * interval);
          break;
        case 'monthly':
          current.setMonth(current.getMonth() + interval);
          break;
        case 'yearly':
          current.setFullYear(current.getFullYear() + interval);
          break;
        default:
          current.setDate(current.getDate() + 7 * interval);
      }
    }

    this.upcomingOccurrences = occurrences;
  }

  getRecurrenceDescription(): string {
    const freq = this.meetingForm.get('recurrenceFrequency')?.value;
    const interval = this.meetingForm.get('recurrenceInterval')?.value || 1;
    const daysOfWeek: string[] = this.meetingForm.get('recurrenceDaysOfWeek')?.value || [];
    const endDate = this.meetingForm.get('recurrenceEndDate')?.value;

    if (!freq) return '';

    const singularLabels: Record<string, string> = {
      'daily': 'day', 'weekly': 'week', 'monthly': 'month', 'yearly': 'year'
    };
    const pluralLabels: Record<string, string> = {
      'daily': 'days', 'weekly': 'weeks', 'monthly': 'months', 'yearly': 'years'
    };
    const freqLabel = interval === 1 ? (singularLabels[freq] || freq) : (pluralLabels[freq] || freq + 's');
    const intervalStr = interval === 1 ? '' : `${interval} `;

    let daysStr = '';
    if (freq === 'weekly' && daysOfWeek.length > 0) {
      daysStr = ' on ' + daysOfWeek.map(d => this.dayOfWeekOptions.find(opt => opt.value === d)?.full || d).join(', ');
    }

    const untilStr = endDate ? ` until ${this.datePipe.transform(endDate, 'MMM d, yyyy')}` : '';

    return `Occurs every ${intervalStr}${freqLabel}${daysStr}${untilStr}`;
  }

  private trySetQuarterAndGoal(): void {
    const m = this.editMeetingData;
    if (!m?.quarter || !this.quarterList?.length) return;

    const quarter = this.quarterList.find((q: any) => q.quarterName === m.quarter);
    if (!quarter) return;

    this.meetingForm.patchValue({ quarter }, { emitEvent: false });

    if (m.hospitalId && m.quarter) {
      this.leaderService.getGoalDictionaryWithQtr(m.hospitalId, m.quarter).subscribe((res: any) => {
        this.goalList = res?.data || [];
        const goal = this.goalList.find((g: any) => g.goalTargetId === m.goalTargetId);
        if (goal) {
          this.meetingForm.patchValue({ affiliatedGoal: goal.goalTargetId }, { emitEvent: false });
        }
      });
    }
  }

  private parseTime(timeStr: string): Date | null {
    if (!timeStr) return null;
    const parts = timeStr.split(':');
    if (parts.length >= 2) {
      const d = new Date();
      d.setHours(parseInt(parts[0], 10), parseInt(parts[1], 10), 0, 0);
      return d;
    }
    return null;
  }

  private parseLocalDate(dateStr: string): Date {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private timeOrderValidator = (group: FormGroup): any | null => {
    const startControl = group.get('startTime');
    const endControl = group.get('endTime');
    const scheduledDateControl = group.get('scheduledDate');

    if (!startControl || !endControl || !scheduledDateControl) return null;

    this.clearControlError(startControl, 'timePassed');
    this.clearControlError(endControl, 'endBeforeStart');

    const validationErrors: Record<string, boolean> = {};
    const startValue = startControl.value;
    const scheduledDateValue = scheduledDateControl.value;

    if (startValue && scheduledDateValue) {
      const startDate = new Date(startValue);
      const scheduledDate = new Date(scheduledDateValue);
      if (!isNaN(startDate.getTime()) && !isNaN(scheduledDate.getTime())) {
        const selectedDateWithStartTime = new Date(scheduledDate);
        selectedDateWithStartTime.setHours(startDate.getHours(), startDate.getMinutes(), startDate.getSeconds(), 0);
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const selectedDateOnly = new Date(scheduledDate.getFullYear(), scheduledDate.getMonth(), scheduledDate.getDate());
        // Skip "timePassed" validation in edit mode — user may be editing a future occurrence
        if (!this.isEditMode && selectedDateOnly.getTime() === today.getTime() && selectedDateWithStartTime.getTime() <= now.getTime()) {
          this.setControlError(startControl, 'timePassed');
          validationErrors['timePassed'] = true;
        }
      }
    }

    const endValue = endControl.value;
    if (startValue && endValue) {
      const startDate = new Date(startValue);
      const endDate = new Date(endValue);
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime()) && endDate.getTime() <= startDate.getTime()) {
        this.setControlError(endControl, 'endBeforeStart');
        validationErrors['endBeforeStart'] = true;
      }
    }

    return Object.keys(validationErrors).length ? validationErrors : null;
  }

  private setControlError(control: AbstractControl, errorKey: string): void {
    const currentErrors = control.errors || {};
    if (!currentErrors[errorKey]) {
      control.setErrors({ ...currentErrors, [errorKey]: true });
    }
  }

  private clearControlError(control: AbstractControl, errorKey: string): void {
    const currentErrors = control.errors;
    if (!currentErrors || !currentErrors[errorKey]) return;
    const { [errorKey]: removedError, ...remainingErrors } = currentErrors;
    void removedError;
    control.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
  }

  searchUserDataList = (searchText: string) => {
    return this.leaderService.searchUserDataList(searchText);
  };

  qtrList() {
    this.leaderService.getQtrList().subscribe((res: any) => {
      this.quarterList = res?.data || [];
      if (this.isEditMode) this.trySetQuarterAndGoal();
    });
  }

  setupHospitalAutocomplete() {
    const hospitalCtrl = this.meetingForm.get('hospital');
    if (hospitalCtrl) {
      hospitalCtrl.valueChanges.pipe(
        startWith(''),
        map(value => this._filterHospitals(value))
      ).subscribe(filtered => {
        this.filteredHospitalList = filtered;
      });
    }
  }

  displayHospitalName(hospital: any): string {
    return hospital && hospital.practiceName ? hospital.practiceName : '';
  }

  private _filterHospitals(value: any): any[] {
    if (!value) return this.hospitals;
    let filterValue = value;
    if (typeof value === 'object' && value.practiceName) filterValue = value.practiceName;
    filterValue = filterValue.toString().toLowerCase();
    return this.hospitals.filter((h: any) => h.practiceName.toLowerCase().includes(filterValue));
  }

  onHospitalChange() {
    const hospital = this.meetingForm.get('hospital')?.value;
    const hospitalId = hospital && typeof hospital === 'object' ? hospital.practiceSKey : null;
    this.meetingForm.patchValue({ hospitalId }, { emitEvent: false });
    this.meetingForm.patchValue({ affiliatedGoal: 0 }, { emitEvent: false });
    this.goalList = [];
    this.loadDataIfBothSelected();
  }

  onQuarterChange() {
    this.meetingForm.patchValue({ affiliatedGoal: 0 }, { emitEvent: false });
    this.goalList = [];
    this.loadDataIfBothSelected();
  }

  loadDataIfBothSelected() {
    const hospital = this.meetingForm.get('hospital')?.value;
    const quarter = this.meetingForm.get('quarter')?.value;
    if (hospital && quarter) this.loadGoals();
  }

  loadGoals() {
    const hospital = this.meetingForm.get('hospital')?.value;
    const quarter = this.meetingForm.get('quarter')?.value;
    if (hospital?.practiceSKey && quarter?.quarterName) {
      this.getGoalList(hospital.practiceSKey, quarter.quarterName);
    }
  }

  getGoalList(hospitalId?: any, qtrId?: any) {
    this.leaderService.getGoalDictionaryWithQtr(hospitalId, qtrId).subscribe((res: any) => {
      this.goalList = res?.data || [];
      if (this.goalList.length === 0) {
        this.leaderService.showNotification('No goals found for the selected hospital and quarter.', true);
      }
    });
  }

  clearHospital(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.meetingForm.get('hospital')?.setValue(null);
    this.meetingForm.get('hospitalId')?.setValue(null);
    this.meetingForm.get('affiliatedGoal')?.setValue(0);
    this.goalList = [];
  }

  onSave(): void {
    if (!this.meetingForm) {
      this.leaderService.showNotification('Fill out all Valid & required fields.', true);
      return;
    }
    this.formSubmitted = true;
    const val = this.meetingForm.value;
    const hospitalId = val?.hospital?.practiceSKey ?? null;
    const Quarter = val?.quarter?.quarterName ?? null;
    const affiliatedGoalId = val?.affiliatedGoal ?? null;
    const attendeeArr = val.attendeesIds;
    if (!attendeeArr || attendeeArr.length === 0) {
      this.leaderService.showNotification('Please select at least one attendee.', true);
      return;
    }
    if (!this.meetingForm.valid) {
      this.meetingForm.markAllAsTouched();
      this.leaderService.showNotification('Fill out all Valid & required fields.', true);
      return;
    }
    if (val.isRecurring && !val.recurrenceFrequency) {
      this.leaderService.showNotification('Please select a recurrence frequency.', true);
      return;
    }
    if (val.isRecurring && !val.recurrenceEndDate) {
      this.leaderService.showNotification('Please select an end date for the recurrence.', true);
      return;
    }
    if (val.isRecurring && val.recurrenceEndDate && val.scheduledDate && val.recurrenceEndDate < val.scheduledDate) {
      this.leaderService.showNotification('Recurrence end date must be after the scheduled date.', true);
      return;
    }
    this.loading = true;

    const attendeeIds = attendeeArr.map((u: any) =>
      u && typeof u === 'object' && u.id !== undefined ? u.id : u
    );

    if (this.isEditMode) {
      this.updateMeeting(attendeeIds, hospitalId, Quarter, affiliatedGoalId);
    } else {
      this.createMeeting(attendeeIds, hospitalId, Quarter, affiliatedGoalId);
    }
  }

  private createMeeting(attendeeIds: number[], hospitalId: any, Quarter: any, affiliatedGoalId: any): void {
    const val = this.meetingForm.value;
    const daysOfWeek = (val.recurrenceDaysOfWeek || []).join(',');
    const request: any = {
      meetingSubject: val.meetingSubject,
      scheduledDate: this.datePipe.transform(val.scheduledDate, 'yyyy-MM-dd'),
      startTime: this.datePipe.transform(val.startTime, 'HH:mm:ss'),
      endTime: this.datePipe.transform(val.endTime, 'HH:mm:ss'),
      HospitalId: hospitalId,
      quarter: Quarter,
      goalTargetId: affiliatedGoalId,
      meetingType: val.meetingType,
      timeZone: val.timezone,
      agenda: val.agenda,
      attendeesIds: attendeeIds,
      isRecurring: val.isRecurring || false,
      recurrencePattern: val.isRecurring ? val.recurrenceFrequency : null,
      recurrenceEndDate: val.isRecurring && val.recurrenceEndDate
        ? this.datePipe.transform(val.recurrenceEndDate, 'yyyy-MM-dd')
        : null,
      recurrenceInterval: val.isRecurring ? val.recurrenceInterval : null,
      recurrenceFrequency: val.isRecurring ? val.recurrenceFrequency : null,
      recurrenceDaysOfWeek: val.isRecurring && daysOfWeek ? daysOfWeek : null
    };

    this.leaderService.createTeamsMeeting(request).subscribe({
      next: (res: any) => {
        this.loading = false;
        const meta = res?.metaData || res?.MetaData;
        const code = meta?.resultDetails?.[0]?.code || meta?.resultDetails?.[0]?.status;
        if (code === 'R00' || code === 'SUCCESS') {
          this.leaderService.showNotification('Meeting created successfully!', false);
          this.meetingForm.reset();
          this.dialogRef.close({ status: 'success', refreshMeetings: true });
        } else {
          const msg = meta?.resultDetails?.[0]?.message || 'Error creating meeting.';
          this.leaderService.showNotification(msg, true);
        }
      },
      error: (err) => {
        this.loading = false;
        console.error('Meeting creation error:', err);
        this.leaderService.showNotification('Error connecting to server.', true);
      }
    });
  }

  private updateMeeting(attendeeIds: number[], hospitalId: any, Quarter: any, affiliatedGoalId: any): void {
    const val = this.meetingForm.value;
    const m = this.editMeetingData;
    const request = {
      meetingId: m.meetingId,
      meetingSubject: val.meetingSubject,
      scheduledDate: this.datePipe.transform(val.scheduledDate, 'yyyy-MM-dd'),
      startTime: this.datePipe.transform(val.startTime, 'HH:mm:ss'),
      endTime: this.datePipe.transform(val.endTime, 'HH:mm:ss'),
      hospitalId: hospitalId,
      quarter: Quarter,
      goalTargetId: affiliatedGoalId,
      meetingType: val.meetingType,
      timeZone: val.timezone,
      agenda: val.agenda,
      attendeesIds: attendeeIds,
      outlookEventId: m.outlookEventId || null,
      joinUrl: m.joinUrl || null
    };

    this.leaderService.updateMeeting(request).subscribe({
      next: (res: any) => {
        this.loading = false;
        const meta = res?.resultDetails || res?.MetaData?.resultDetails;
        const code = meta?.[0]?.code || meta?.[0]?.status || res?.resultDetails?.[0]?.code;
        if (code === 'R00' || code === 'SUCCESS') {
          this.leaderService.showNotification('Meeting updated successfully!', false);
          this.dialogRef.close({ status: 'updated', refreshMeetings: true });
        } else {
          const msg = meta?.[0]?.message || 'Error updating meeting.';
          this.leaderService.showNotification(msg, true);
        }
      },
      error: (err) => {
        this.loading = false;
        console.error('Meeting update error:', err);
        this.leaderService.showNotification('Error connecting to server.', true);
      }
    });
  }

  closeModal() {
    this.dialogRef.close('closed');
    this.meetingForm.reset();
  }
}
